/**
 * probas — outillage du dossier probabilités (4e, attendus 166 à 170).
 *
 * Revue 2026-09-25 (vague « sept objectifs 4e fermés ») : les figures de
 * juillet (svgUrneVar avec son vase trop haut et ses boules rangées par
 * couleur, svgCible, svgDamier sans alt) et le filet construireChoixFrac (il
 * bouchait les QCM avec des fractions inventées) sont RETIRÉS ; tout ce qui
 * sert 166 (svgUrneNumeros, elUrneNumeros, COULEURS_4E, cap) est gardé à
 * l'identique, le rendu de 166 ne change pas d'un octet (vérifié contre
 * l'ancien fichier).
 *
 * Figures neuves, toutes en imageUrl (data-URL SVG) avec un `alt` fourni par le
 * gen : urne à boules mélangées (170), roue à secteurs égaux (170), sac de
 * jetons (170), damier à cases coloriées (168), cible rectangulaire à zone
 * cotée (168), cible carrée à disque (168). Aucune lettre dans les SVG (les
 * cotes portent la mesure seule : « 3 cm »), donc aucune police KaTeX à charger.
 *
 * Nombres : arithmétique ENTIÈRE en centièmes (frCent) ou dix-millièmes
 * (frDixMillieme) ; jamais un flottant mis en texte.
 */
import { imgElement } from '../../5eme/statistiques/svgStats';

export { imgElement } from '../../5eme/statistiques/svgStats';

/** Le rappel brouillon de l'équipe, à poser juste avant la question. */
export const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Pose tes calculs au brouillon.</p>';

// ─── Palette des boules/objets (adjectifs féminins singuliers) ───

export const COULEURS_4E = {
    rouge:   { fill: '#dc2626', texte: '#fff' },
    orange:  { fill: '#f97316', texte: '#fff' },
    bleue:   { fill: '#1d4ed8', texte: '#fff' },
    verte:   { fill: '#16a34a', texte: '#fff' },
    jaune:   { fill: '#eab308', texte: '#1e293b' },
    violette:{ fill: '#7c3aed', texte: '#fff' },
    noire:   { fill: '#1e293b', texte: '#fff' },
    blanche: { fill: '#f8fafc', texte: '#1e293b' },
    grise:   { fill: '#94a3b8', texte: '#1e293b' },
};

const SVG_OUVRE = (W, H) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial">`;

/** Le vase de l'urne (col évasé, panse droite, fond arrondi), commun à 166 et 170. */
const traceVase = (vx, vw, vTop, vBot, W) => `<path d="M ${vx} ${vTop} C ${vx} ${vTop - 8}, ${vx + vw * 0.28} ${vTop - 12}, ${W / 2} ${vTop - 12} `
    + `C ${vx + vw * 0.72} ${vTop - 12}, ${vx + vw} ${vTop - 8}, ${vx + vw} ${vTop} `
    + `L ${vx + vw - 8} ${vBot} C ${vx + vw - 10} ${vBot + 14}, ${vx + 10} ${vBot + 14}, ${vx + 8} ${vBot} Z" `
    + 'fill="#f1f5f9" stroke="#334155" stroke-width="2.5"/>';

/**
 * svgUrneNumeros(boules) — AJOUT revue 2026-09-23 (gen_166). Urne de l'exemple
 * de réussite de fin de 4e : boules rangées par quatre (trois rangées pour
 * douze boules), couleurs et numéros MÉLANGÉS dans l'ordre donné, numéros
 * répétés possibles ; le vase épouse les rangées (plus de grand vide sous les
 * boules). `boules` : [{ couleur (clé de COULEURS_4E), numero }].
 * Rendu inchangé par la revue du 2026-09-25 (166 non touché).
 */
export const svgUrneNumeros = (boules) => {
    const n = boules.length;
    const parLigne = 4;
    const lignes = Math.ceil(n / parLigne);
    const r = 20, dx = 50, dy = 48;
    const W = 262;
    const vw = 234, vx = (W - vw) / 2, vTop = 30;
    const cy0 = vTop + 12 + r;
    const vBot = cy0 + (lignes - 1) * dy + r + 12;
    const H = vBot + 16;
    const vase = traceVase(vx, vw, vTop, vBot, W);
    let cercles = '';
    boules.forEach((b, i) => {
        const row = Math.floor(i / parLigne);
        const dansLigne = Math.min(parLigne, n - row * parLigne);
        const cx = W / 2 - ((dansLigne - 1) * dx) / 2 + (i % parLigne) * dx;
        const cy = cy0 + row * dy;
        const c = COULEURS_4E[b.couleur] || COULEURS_4E.grise;
        cercles += `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${c.fill}" stroke="#0f172a" stroke-width="1.5"/>`
            + `<text x="${cx}" y="${cy + 5}" font-size="15" font-weight="bold" fill="${c.texte}" text-anchor="middle">${b.numero}</text>`;
    });
    return `${SVG_OUVRE(W, H)}${vase}${cercles}</svg>`;
};

/**
 * svgUrneBoules(boules) — urne de 170 : boules SANS numéro, dans l'ordre donné
 * (le gen les mélange), quatre par rangée jusqu'à douze boules, cinq au-delà
 * (vingt-cinq boules tiennent en cinq rangées) ; le vase épouse les rangées,
 * aucun vide sous les boules. `boules` : [{ couleur (clé de COULEURS_4E) }].
 */
export const svgUrneBoules = (boules) => {
    const n = boules.length;
    const parLigne = n > 12 ? 5 : 4;
    const lignes = Math.ceil(n / parLigne);
    const r = 20, dx = 50, dy = 48;
    const W = 62 + parLigne * dx;
    const vw = W - 28, vx = (W - vw) / 2, vTop = 30;
    const cy0 = vTop + 12 + r;
    const vBot = cy0 + (lignes - 1) * dy + r + 12;
    const H = vBot + 16;
    let cercles = '';
    boules.forEach((b, i) => {
        const row = Math.floor(i / parLigne);
        const dansLigne = Math.min(parLigne, n - row * parLigne);
        const cx = W / 2 - ((dansLigne - 1) * dx) / 2 + (i % parLigne) * dx;
        const cy = cy0 + row * dy;
        const c = COULEURS_4E[b.couleur] || COULEURS_4E.grise;
        cercles += `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${c.fill}" stroke="#0f172a" stroke-width="1.5"/>`;
    });
    return `${SVG_OUVRE(W, H)}${traceVase(vx, vw, vTop, vBot, W)}${cercles}</svg>`;
};

/**
 * svgSacJetons(jetons) — sac de 170 : une bourse (col resserré, panse ronde)
 * qui contient les jetons dans l'ordre donné, même disposition que l'urne.
 */
export const svgSacJetons = (jetons) => {
    const n = jetons.length;
    const parLigne = n > 12 ? 5 : 4;
    const lignes = Math.ceil(n / parLigne);
    const r = 18, dx = 46, dy = 44;
    const W = 70 + parLigne * dx;
    const colY = 34, panseTop = 62;
    const cy0 = panseTop + 10 + r;
    const bas = cy0 + (lignes - 1) * dy + r + 12;
    const H = bas + 22;
    const col = `<path d="M ${W / 2 - 34} ${colY} L ${W / 2 + 34} ${colY} L ${W / 2 + 26} ${colY - 18} L ${W / 2 - 26} ${colY - 18} Z" fill="#e2e8f0" stroke="#334155" stroke-width="2.5"/>`;
    const panse = `<path d="M ${W / 2 - 30} ${colY} C ${W / 2 - 30} ${panseTop}, 14 ${panseTop}, 14 ${(panseTop + bas) / 2} `
        + `C 14 ${bas + 10}, ${W / 2 - 60} ${bas + 18}, ${W / 2} ${bas + 18} `
        + `C ${W / 2 + 60} ${bas + 18}, ${W - 14} ${bas + 10}, ${W - 14} ${(panseTop + bas) / 2} `
        + `C ${W - 14} ${panseTop}, ${W / 2 + 30} ${panseTop}, ${W / 2 + 30} ${colY} Z" fill="#f1f5f9" stroke="#334155" stroke-width="2.5"/>`;
    let cercles = '';
    jetons.forEach((j, i) => {
        const row = Math.floor(i / parLigne);
        const dansLigne = Math.min(parLigne, n - row * parLigne);
        const cx = W / 2 - ((dansLigne - 1) * dx) / 2 + (i % parLigne) * dx;
        const cy = cy0 + row * dy;
        const c = COULEURS_4E[j.couleur] || COULEURS_4E.grise;
        cercles += `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${c.fill}" stroke="#0f172a" stroke-width="1.5"/>`;
    });
    return `${SVG_OUVRE(W, H)}${panse}${col}${cercles}</svg>`;
};

/**
 * svgRoueSecteurs(couleurs) — roue de loterie à secteurs ÉGAUX (autant que
 * d'entrées), colorés dans l'ordre donné (le gen mélange), flèche fixe en haut.
 * `couleurs` : clés de COULEURS_4E.
 */
export const svgRoueSecteurs = (couleurs) => {
    const n = couleurs.length;
    const r = 96, cx = 116, cy = 124;
    const W = 232, H = 232;
    const pas = 360 / n;
    let dessin = '';
    couleurs.forEach((c, i) => {
        const a0 = ((i * pas - 90) * Math.PI) / 180;
        const a1 = (((i + 1) * pas - 90) * Math.PI) / 180;
        const x0 = (cx + r * Math.cos(a0)).toFixed(1), y0 = (cy + r * Math.sin(a0)).toFixed(1);
        const x1 = (cx + r * Math.cos(a1)).toFixed(1), y1 = (cy + r * Math.sin(a1)).toFixed(1);
        const large = pas > 180 ? 1 : 0;
        const fill = (COULEURS_4E[c] || COULEURS_4E.grise).fill;
        dessin += `<path d="M ${cx} ${cy} L ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1} Z" fill="${fill}" stroke="#fff" stroke-width="1.5"/>`;
    });
    dessin += `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="#334155" stroke-width="2.5"/>`
        + `<circle cx="${cx}" cy="${cy}" r="7" fill="#1e293b"/>`
        + `<path d="M ${cx - 9} 8 L ${cx + 9} 8 L ${cx} 28 Z" fill="#1e293b"/>`;
    return `${SVG_OUVRE(W, H)}${dessin}</svg>`;
};

/** Couleur des cases coloriées du damier et de la zone favorable des cibles. */
export const BLEU_FAVORABLE = '#1E3A8A';

/**
 * svgDamier(lignes, colonnes, favorables) — grille de cases ÉGALES ; les cases
 * d'index (0-based, ligne par ligne) présentes dans `favorables` sont coloriées
 * en BLEU_FAVORABLE, les autres sont blanches. Aucun nombre sur la figure : le
 * total et les cases coloriées se COMPTENT.
 */
export const svgDamier = (lignes, colonnes, favorables) => {
    const cell = 36, m = 6;
    const W = colonnes * cell + 2 * m, H = lignes * cell + 2 * m;
    const fav = new Set(favorables);
    let cases = '';
    for (let r = 0; r < lignes; r++) {
        for (let c = 0; c < colonnes; c++) {
            const idx = r * colonnes + c;
            const x = m + c * cell, y = m + r * cell;
            const fill = fav.has(idx) ? BLEU_FAVORABLE : '#ffffff';
            cases += `<rect x="${x}" y="${y}" width="${cell}" height="${cell}" fill="${fill}" stroke="#334155" stroke-width="1.5"/>`;
        }
    }
    return `${SVG_OUVRE(W, H)}${cases}</svg>`;
};

const cote = (x1, y1, x2, y2) => `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="#475569" stroke-width="1.5"/>`;
const etiquette = (x, y, txt, anchor = 'middle', fill = '#1e293b') =>
    `<text x="${x}" y="${y}" font-size="13" fill="${fill}" text-anchor="${anchor}">${txt}</text>`;

/**
 * svgCibleRectangle(L, l, a, b) — cible rectangulaire de L sur l (cm) avec une
 * zone bleue de a sur b (cm) centrée. Figure PROPORTIONNELLE : le grand côté
 * mesure 220 px. Cotes : L sous la cible, l à sa droite ; la largeur a de la
 * zone DANS la zone (en blanc) quand elle est assez haute, sinon au-dessus ;
 * sa hauteur b dans la zone à droite quand elle est assez large, sinon à côté
 * (à droite s'il reste de la place avant la cote de la cible, à gauche sinon :
 * sur une zone de 16 cm dans 18, « 5 cm » chevauchait « 15 cm », vu en PNG).
 * La mesure seule, sans lettre.
 */
export const svgCibleRectangle = (L, l, a, b) => {
    const echelle = 220 / Math.max(L, l);
    const wL = L * echelle, hl = l * echelle, wa = a * echelle, hb = b * echelle;
    const x0 = 16, y0 = 16;
    const zx = x0 + (wL - wa) / 2, zy = y0 + (hl - hb) / 2;
    const W = x0 + wL + 62, H = y0 + hl + 40;
    const largeurTexte = 40;
    const coteA = hb >= 30 && wa >= 44
        ? etiquette((zx + wa / 2).toFixed(1), (zy + 14).toFixed(1), `${a} cm`, 'middle', '#fff')
        : etiquette((zx + wa / 2).toFixed(1), (zy - 5).toFixed(1), `${a} cm`);
    let coteB = '';
    if (a !== b) {
        if (wa >= 44 && hb >= 16) coteB = etiquette((zx + wa - 5).toFixed(1), (zy + hb / 2 + 4).toFixed(1), `${b} cm`, 'end', '#fff');
        else if (zx + wa + 5 + largeurTexte <= x0 + wL + 6) coteB = etiquette((zx + wa + 5).toFixed(1), (zy + hb / 2 + 4).toFixed(1), `${b} cm`, 'start');
        else coteB = etiquette((zx - 5).toFixed(1), (zy + hb / 2 + 4).toFixed(1), `${b} cm`, 'end');
    }
    return `${SVG_OUVRE(W, H)}`
        + `<rect x="${x0}" y="${y0}" width="${wL}" height="${hl}" fill="#e2e8f0" stroke="#334155" stroke-width="2.5"/>`
        + `<rect x="${zx.toFixed(1)}" y="${zy.toFixed(1)}" width="${wa.toFixed(1)}" height="${hb.toFixed(1)}" fill="${BLEU_FAVORABLE}" fill-opacity="0.85" stroke="#0f172a" stroke-width="1.5"/>`
        + cote(x0, y0 + hl + 12, x0 + wL, y0 + hl + 12)
        + etiquette(x0 + wL / 2, y0 + hl + 28, `${L} cm`)
        + cote(x0 + wL + 12, y0, x0 + wL + 12, y0 + hl)
        + etiquette(x0 + wL + 18, y0 + hl / 2 + 4, `${l} cm`, 'start')
        + coteA + coteB
        + '</svg>';
};

/**
 * svgCibleDisque(S, r) — cible carrée de côté S (cm) avec un disque bleu de
 * rayon r (cm) au centre. Figure PROPORTIONNELLE (carré de 200 px). Cotes : le
 * côté sous le carré, le rayon sur un segment centre → bord du disque ; la
 * mesure seule, sans lettre.
 */
export const svgCibleDisque = (S, r) => {
    const px = 200, m = 40;
    const echelle = px / S;
    const rpx = r * echelle;
    const x0 = m, y0 = 24, cx = x0 + px / 2, cy = y0 + px / 2;
    const W = px + 2 * m, H = px + y0 + 44;
    const label = `${r} cm`;
    return `${SVG_OUVRE(W, H)}`
        + `<rect x="${x0}" y="${y0}" width="${px}" height="${px}" fill="#e2e8f0" stroke="#334155" stroke-width="2.5"/>`
        + `<circle cx="${cx}" cy="${cy}" r="${rpx.toFixed(1)}" fill="${BLEU_FAVORABLE}" fill-opacity="0.85" stroke="#0f172a" stroke-width="2"/>`
        + cote(x0, y0 + px + 14, x0 + px, y0 + px + 14)
        + etiquette(cx, y0 + px + 30, `${S} cm`)
        + `<line x1="${cx}" y1="${cy}" x2="${(cx + rpx).toFixed(1)}" y2="${cy}" stroke="#fff" stroke-width="1.5"/>`
        + (rpx >= 44
            ? etiquette((cx + rpx / 2).toFixed(1), cy - 6, label, 'middle', '#fff')
            : etiquette((cx + rpx + 5).toFixed(1), cy + 4, label, 'start'))
        + '</svg>';
};

// ─── Emballages (énoncé : imageUrl + alt) ─────────────────────

const avecAlt = (svg, alt, maxWidth) => {
    const el = imgElement(svg, maxWidth);
    el.elements[0].config.alt = alt;
    return el;
};

/**
 * Élément additionnel d'énoncé : l'urne à numéros répétés (svgUrneNumeros) avec
 * son `alt` (lu à l'écran et à l'impression quand l'image manque). AJOUT revue
 * 2026-09-23 (gen_166).
 */
export const elUrneNumeros = (boules, alt, maxWidth = 'small') => avecAlt(svgUrneNumeros(boules), alt, maxWidth);
/** Urne de boules mélangées, sans numéro (170). */
export const elUrneBoules = (boules, alt, maxWidth = 'small') => avecAlt(svgUrneBoules(boules), alt, maxWidth);
/** Sac de jetons (170). */
export const elSacJetons = (jetons, alt, maxWidth = 'small') => avecAlt(svgSacJetons(jetons), alt, maxWidth);
/** Roue à secteurs égaux (170). */
export const elRoueSecteurs = (couleurs, alt, maxWidth = 'compact') => avecAlt(svgRoueSecteurs(couleurs), alt, maxWidth);
/** Damier (168). */
export const elDamier = (l, c, fav, alt, maxWidth = 'compact') => avecAlt(svgDamier(l, c, fav), alt, maxWidth);
/** Cible rectangulaire à zone cotée (168). */
export const elCibleRectangle = (L, l, a, b, alt, maxWidth = 'compact') => avecAlt(svgCibleRectangle(L, l, a, b), alt, maxWidth);
/** Cible carrée à disque (168). */
export const elCibleDisque = (S, r, alt, maxWidth = 'compact') => avecAlt(svgCibleDisque(S, r), alt, maxWidth);

// ─── Helpers numériques exacts (entiers) ─────────────────────

/** pgcd (Euclide, entiers positifs). */
export const pgcd = (a, b) => (b === 0 ? a : pgcd(b, a % b));

/** Fraction a/b irréductible → "p/q" (dénominateur toujours positif). */
export const fracIrr = (a, b) => {
    const s = b < 0 ? -1 : 1;
    const na = a * s, nb = b * s;
    const g = pgcd(Math.abs(na), Math.abs(nb)) || 1;
    return `${na / g}/${nb / g}`;
};

/**
 * Un entier de centièmes → écriture décimale française sans zéro inutile :
 * frCent(35) = « 0,35 », frCent(40) = « 0,4 », frCent(5) = « 0,05 »,
 * frCent(135) = « 1,35 », frCent(100) = « 1 », frCent(9960) = « 99,6 ».
 * Jamais de flottant : c'est de l'arithmétique sur des entiers.
 */
export const frCent = (c) => {
    const n = Math.round(c);
    const signe = n < 0 ? '-' : '';
    const abs = Math.abs(n);
    const entier = Math.floor(abs / 100);
    const reste = abs % 100;
    if (reste === 0) return `${signe}${entier}`;
    const deux = String(reste).padStart(2, '0').replace(/0$/, '');
    return `${signe}${entier},${deux}`;
};

/** Un entier de dix-millièmes → écriture décimale française sans zéro inutile (frDixMillieme(525) = « 0,0525 »). */
export const frDixMillieme = (d) => {
    const n = Math.round(d);
    const entier = Math.floor(n / 10000);
    const reste = n % 10000;
    if (reste === 0) return String(entier);
    return `${entier},${String(reste).padStart(4, '0').replace(/0+$/, '')}`;
};

/** Écriture française d'un nombre : point → virgule, sans zéros finaux (réservé aux entiers et aux valeurs déjà exactes). */
export const frNb = (x) => {
    let s = String(x);
    if (s.includes('.')) s = s.replace(/0+$/, '').replace(/\.$/, '');
    return s.replace('.', ',');
};

/**
 * Les écritures fractionnaires ÉGALES à num/den qu'un élève produit : la
 * fraction comptée en tête (« 4/6 »), toutes ses simplifications (un diviseur
 * du pgcd : « 2/3 »), et son écriture en dixièmes, centièmes ou millièmes
 * quand elle existe (« 5/10 », « 50/100 », « 500/1000 » pour 3/6). Liste
 * courte, finie et auditable pour contenu.reponses_acceptees (comparaison
 * d'expression, règle d'équipe : toute écriture égale est juste).
 */
export const ecrituresFraction = (num, den) => {
    const g = pgcd(num, den) || 1;
    const out = [];
    const ajoute = (n, d) => { const e = `${n}/${d}`; if (!out.includes(e)) out.push(e); };
    for (let k = 1; k <= g; k++) if (g % k === 0) ajoute(num / k, den / k);
    const n0 = num / g, d0 = den / g;
    [10, 100, 1000].forEach((D) => { if (D % d0 === 0) ajoute((n0 * D) / d0, D); });
    return out;
};

/**
 * L'écriture décimale EXACTE de num/den (chaîne française) si le dénominateur
 * réduit divise 10 000, sinon null ; avec ses variantes à zéros finaux
 * (« 0,1 », « 0,10 », « 0,100 ») pour la liste des écritures admises.
 */
export const ecrituresDecimales = (num, den) => {
    for (let p = 0; p <= 4; p++) {
        const d = 10 ** p;
        if ((num * d) % den === 0) {
            const entier = Math.floor(num / den);
            const partie = (num * d) / den - entier * d;
            const base = p === 0 ? String(entier) : `${entier},${String(partie).padStart(p, '0')}`;
            const out = [base];
            for (let z = p + 1; z <= 4; z++) out.push(p === 0 ? `${entier},${'0'.repeat(z)}` : `${base}${'0'.repeat(z - p)}`);
            return out;
        }
    }
    return null;
};

// ─── Élisions françaises ─────────────────────────────────────

const VOYELLE = /^[AEIOUYHÀÂÉÈÊËÎÏÔÙÛaeiouyhàâéèêëîïôùû]/;
export const dElide = (mot) => (VOYELLE.test(mot) ? `d'${mot}` : `de ${mot}`);
export const queElide = (mot) => (VOYELLE.test(mot) ? `qu'${mot}` : `que ${mot}`);
export const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

/** « 3 boules rouges », « 1 boule rouge », « 4 boules orange » (orange est invariable). */
export const denombre = (n, nom, adjectif, invariable = false) =>
    `${n} ${nom}${n > 1 ? 's' : ''}${adjectif ? ` ${adjectif}${n > 1 && !invariable ? 's' : ''}` : ''}`;
